import React, { useState, useEffect, useCallback } from "react";
import cn from "classnames";
import styles from "./StudentDetail.module.sass";
import { useLocation, useParams, useNavigate, Link } from "react-router-dom";

// Screens
import FolderProgress from "./FolderProgress";
import ContractProgress from "./ContractProgress";
import Comptability from "./Comptability";


// Components
import Card from "../../components/Card";
import Dropdown from "../../components/Dropdown";
import LetterAvatar from "../../components/LetterAvatar";
import Icon from "../../components/Icon";
import Actions from "../../components/Actions";
import ModalRupture from "../../components/ModalRupture";
import ModalChangeOpco from "../../components/ModalChangeOpco";
import ModalAssignCompany from "../../components/ModalAssignCompany";
import ModalChangeCenter from "../../components/ModalChangeCenter";
import ModalChangeCompany from "../../components/ModalChangeCompany";
import ModalEngageOpco from "../../components/ModalEngageOpco";

// Services
import { getFolderById } from "../../services/folder.service";
import { generateCertificat, generateDocuments } from "../../services/contract.service";
import { sendToOpco, statusFolderOpco } from "../../services/opco.service";
import { toast } from "react-toastify";
import { showSuccess, showError } from "../../services/toast.service";


// data
const navigation = ["Avancement", "Documents & Signature", "Financement"];

const FolderDetail = () => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [folder, setFolder] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [ruptureModalVisible, setRuptureModalVisible] = useState(false);
  const [opcoModalVisible, setOpcoModalVisible] = useState(false);
  const [assignCompanyModalVisible, setAssignCompanyModalVisible] = useState(false);
  const [changeCenterModalVisible, setChangeCenterModalVisible] = useState(false);
  const [changeCompanyModalVisible, setChangeCompanyModalVisible] = useState(false);
  const [engageOpcoModalVisible, setEngageOpcoModalVisible] = useState(false);
  const location = useLocation();
  const { id } = useParams();

  // Récupérer le contrat depuis le dossier s'il existe
  const contract = folder?.expand?.contract_id;
  const [currentOpco, setCurrentOpco] = useState(contract?.assigned_opco || contract?.opco || "");

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(date);
    } catch (e) {
      return dateString;
    }
  }, []);

  const handleModalClose = () => {
    setModalVisible(false);
  };

  const handleOpcoModalClose = () => {
    setOpcoModalVisible(false);
  };

  const handleOpcoUpdated = (newOpco) => {
    setCurrentOpco(newOpco);
  };

  // Fonction pour ouvrir la modal de rupture
  const handleGenerateRupture = () => {
    if (!contract?.id) {
      showError("Aucun contrat associé à ce dossier");
      return;
    }
    setRuptureModalVisible(true);
  };

  const handleRuptureModalClose = () => {
    setRuptureModalVisible(false);
  };

  // Fonction pour charger les données du dossier
  const loadFolder = useCallback(async () => {
    try {
      const data = await getFolderById(id, { expand: 'student_id,company_id,formation_id,promotion_id,center_id,contract_id' });
      setFolder(data);
    } catch (err) {
      console.error('Erreur:', err);
    }
  }, [id]);

  // Gérer l'association d'une entreprise au dossier
  const handleCompanyAssigned = (company) => {
    // Recharger les données du dossier pour afficher la nouvelle entreprise
    loadFolder();
  };

  // Gérer le changement de centre de formation
  const handleCenterChanged = (center) => {
    // Recharger les données du dossier pour afficher le nouveau centre
    loadFolder();
  };

  // Fonction pour modifier le centre de formation
  const handleChangeCenter = () => {
    setChangeCenterModalVisible(true);
  };

  // Fonction pour ajouter une entreprise
  const handleAddCompany = () => {
    setAssignCompanyModalVisible(true);
  };

  // Fonction pour modifier une entreprise existante
  const handleModifyCompany = () => {
    setChangeCompanyModalVisible(true);
  };

  // Fonction pour engager un dossier OPCO
  const handleEngageOpco = () => {
    if (!contract?.id) {
      showError("Aucun contrat associé à ce dossier");
      return;
    }
    setEngageOpcoModalVisible(true);
  };

  // Fonction pour gérer la mise à jour après engagement
  const handleEngagementUpdated = (numeroInterne) => {
    // Recharger les données du dossier pour refléter les changements
    loadFolder();
  };

  // Fonction pour générer le CERFA (même logique que dans le Stepper)
  const handleGenerateCerfa = async () => {
    try {
      if (!folder?.id) {
        showError("Aucun dossier sélectionné");
        return;
      }

      // Appeler la fonction de génération de documents avec le folderId
      const result = await generateDocuments(folder.id);

      if (result) {
        showSuccess("Documents générés avec succès");
        console.log('Résultat de la génération:', result);
        // Recharger les données du dossier pour mettre à jour la progression
        loadFolder();
      }
    } catch (error) {
      console.error("Erreur lors de la génération du CERFA:", error);
      showError("Erreur lors de la génération des documents");
    }
  };

  const handleSendToOpco = async () => {
    if (!id) {
      toast.error("Aucun dossier sélectionné");
      return;
    }
    try {
      toast.info("Envoi au opco en cours...");
      const result = await sendToOpco(id);
      toast.success("Envoi au opco effectué avec succès");
    } catch (error) {
      console.error("Erreur lors de l'envoi au opco:", error);
      toast.error("Erreur lors de l'envoi au opco");
    }
  };

  const handleGenerateCertificat = async () => {
    if (!folder?.id) {
      toast.error("Aucun dossier sélectionné");
      return;
    }

    if (!folder.expand?.contract_id) {
      toast.error("Aucun contrat associé à ce dossier");
      return;
    }

    try {
      toast.info("Génération du certificat en cours...");
      const { blob, fileName } = await generateCertificat(folder.id);

      // Create a link to download the file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = fileName;

      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success("Certificat téléchargé avec succès !");
    } catch (error) {
      console.error("Erreur lors de la génération du certificat:", error);
      toast.error(error.message || "Erreur lors de la génération du certificat");
    }
  };

  useEffect(() => {
    if (location.state?.folder) {
      setFolder(location.state.folder);
      return;
    }

    loadFolder();
  }, [id, location.state, loadFolder]);

  // Récupérer les données depuis le dossier
  const student = folder?.expand?.student_id || {};
  const company = folder?.expand?.company_id || {};
  const center = folder?.expand?.center_id || {};

  // Fonctions de navigation
  const handleNavigateToStudent = () => {
    if (student?.id) {
      navigate(`/admin/students/${student.id}`, { state: { student } });
    }
  };

  const handleNavigateToCompany = () => {
    if (company?.id) {
      navigate(`/admin/companies/${company.id}`, { state: { company } });
    }
  };

  const handleNavigateToCenter = () => {
    if (center?.id) {
      navigate(`/admin/centers/${center.id}`, { state: { center } });
    }
  };

  // Vérifier si un document CERFA existe déjà
  const hasCerfaDocument = contract?.document && contract.document.trim() !== "";
  
  // Définition des actions pour le composant Actions (après définition des variables)
  const actionsWithHandlers = [
    {
      title: "Modifier l'opco",
      action: () => setOpcoModalVisible(true)
    },
    {
      title: company?.id ? "Modifier l'entreprise" : "Ajouter une entreprise",
      action: company?.id ? () => handleModifyCompany() : () => handleAddCompany()
    },
    {
      title: "Modifier le centre",
      action: () => handleChangeCenter()
    },
    {
      title: "Engager un dossier",
      action: () => handleEngageOpco()
    },
    {
      title: "Envoyer au opco",
      action: () => handleSendToOpco()
    },
    {
      title: hasCerfaDocument ? "Regénérer le CERFA" : "Générer le CERFA",
      action: () => handleGenerateCerfa()
    },
    {
      title: "Générer une rupture",
      action: () => handleGenerateRupture()
    },
    {
      title: "Générer un certificat",
      action: () => handleGenerateCertificat()
    }
  ];

  // Récupérer les données de progression
  const progression = folder?.progression || {};

  // Fonctions de calcul de progression
  const calculateStudentProgress = () => {
    const studentData = progression.etudiant || {};
    const completed = Object.values(studentData).filter(value => value === true).length;
    const total = Object.keys(studentData).length;
    return total > 0 ? (completed / total) * 100 : 0;
  };

  const calculateCompanyProgress = () => {
    const companyData = progression.entreprise || {};
    const completed = Object.values(companyData).filter(value => value === true).length;
    const total = Object.keys(companyData).length;
    return total > 0 ? (completed / total) * 100 : 0;
  };

  const calculateSchoolProgress = () => {
    const schoolData = progression.ecole || {};
    const completed = Object.values(schoolData).filter(value => value === true).length;
    const total = Object.keys(schoolData).length;
    return total > 0 ? (completed / total) * 100 : 0;
  };

  // Calculer les pourcentages et les données de progression
  const studentProgressPercent = calculateStudentProgress();
  const companyProgressPercent = calculateCompanyProgress();
  const schoolProgressPercent = calculateSchoolProgress();

  // Calculer les étapes complétées pour chaque section
  const getProgressData = (sectionName) => {
    const sectionData = progression[sectionName] || {};
    const completedSteps = Object.values(sectionData).filter(value => value === true).length;
    const totalSteps = Object.keys(sectionData).length;
    const progressPercent = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;
    const isComplete = progressPercent === 100;

    return {
      completedSteps,
      totalSteps,
      progressPercent,
      progressWidth: `${progressPercent}%`,
      progressColor: isComplete ? "#10B981" : "#78C4D1",
      progressLabel: isComplete ? 'Complet' : 'En cours'
    };
  };

  const studentProgress = getProgressData('etudiant');
  const companyProgress = getProgressData('entreprise');
  const schoolProgress = getProgressData('ecole');

  return (
    <>
      <div className={styles.shop}>
        <Link className={cn("button-white", styles.button)} to="/admin/folders">
          <Icon name="arrow-left" />
          Retour
        </Link>

        <div className={styles.background}>
          <img src="/images/content/bg-shop.jpg" alt="Background" />
        </div>

        {folder && (
          <>
            <ModalRupture
              visible={ruptureModalVisible}
              onClose={handleRuptureModalClose}
              folderId={folder?.id}
            />
            <ModalChangeOpco
              visible={opcoModalVisible}
              onClose={handleOpcoModalClose}
              folderId={folder?.id}
              currentOpco={currentOpco}
              onOpcoUpdated={handleOpcoUpdated}
            />
            <ModalAssignCompany
              visible={assignCompanyModalVisible}
              onClose={() => setAssignCompanyModalVisible(false)}
              onSave={handleCompanyAssigned}
              folderId={id}
            />
            <ModalChangeCenter
              visible={changeCenterModalVisible}
              onClose={() => setChangeCenterModalVisible(false)}
              onSave={handleCenterChanged}
              folderId={id}
              currentCenterId={center?.id}
            />
            <ModalChangeCompany
              visible={changeCompanyModalVisible}
              onClose={() => setChangeCompanyModalVisible(false)}
              onSave={handleCompanyAssigned}
              folderId={id}
              currentCompanyId={company?.id}
            />
            <ModalEngageOpco
              visible={engageOpcoModalVisible}
              onClose={() => setEngageOpcoModalVisible(false)}
              contractId={contract?.id}
              onEngagementUpdated={handleEngagementUpdated}
            />

            <Card
              className={styles.card}
              title={
                <>
                  <span>Dossier : {folder.id}</span>
                  {contract && (
                    <span className={cn('status-type', 'status-green-dark')} style={{ marginLeft: '10px' }}>
                      {contract.id}
                    </span>
                  )}
                  {folder.expand?.opco_id?.name && (
                    <span className={cn('status-type', 'status-blue-dark')} style={{ marginLeft: '10px' }}>
                      {folder.expand.opco_id.name}
                    </span>
                  )}
                </>
              }
              classTitle="title-red"
              head={
                <Actions
                  className={styles.actions}
                  classActionsHead={styles.actionsHead}
                  items={actionsWithHandlers}
                  small
                />
              }
            >
              <div className={styles.overview}>
                <div className={styles.list}>
                  {/* Étudiant */}
                  <div 
                    className={styles.item} 
                    style={{ backgroundColor: "#edf8f2", cursor: student?.id ? 'pointer' : 'default' }}
                    onClick={handleNavigateToStudent}
                  >
                    <div className={styles.avatar}>
                      <LetterAvatar
                        firstName={folder.expand?.student_id?.first_name}
                        lastName={folder.expand?.student_id?.last_name}
                      />
                    </div>
                    <div className={styles.line}>
                      <div className={styles.details}>
                        <div className={styles.indicator}>
                          <span>Étudiant </span>
                        </div>
                        <div className={styles.category}>
                          {student.first_name ? `${student.first_name} ${student.last_name}` : 'Non spécifié'}
                        </div>
                      </div>
                    </div>
                    <div className={styles.progressContainer}>
                      <div className={styles.progressBox}>
                        <div
                          className={styles.progressNumber}
                          style={{ color: studentProgress.progressColor }}
                        >
                          {studentProgress.completedSteps}/{studentProgress.totalSteps}
                        </div>
                        <div className={styles.progressLine}>
                          <div
                            className={styles.progressBar}
                            style={{
                              backgroundColor: studentProgress.progressColor,
                              width: studentProgress.progressWidth,
                            }}
                          ></div>
                        </div>
                      </div>
                      <div className={styles.progressLabel}>
                        {studentProgress.progressLabel}
                      </div>
                    </div>
                  </div>

                  {/* Entreprise */}
                  <div 
                    className={styles.item} 
                    style={{ backgroundColor: "#edf8f2", cursor: company?.id ? 'pointer' : 'default' }}
                    onClick={handleNavigateToCompany}
                  >
                    <div className={styles.avatar}>
                      <img src="/images/content/company_logo.png" alt="Entreprise" />
                    </div>
                    <div className={styles.line}>
                      <div className={styles.details}>
                        <div className={styles.indicator}>
                          <span>Entreprise </span>
                        </div>
                        <div className={styles.category}>
                          {company.name || 'Non spécifiée'}
                        </div>
                      </div>
                    </div>
                    <div className={styles.progressContainer}>
                      <div className={styles.progressBox}>
                        <div
                          className={styles.progressNumber}
                          style={{ color: companyProgress.progressColor }}
                        >
                          {companyProgress.completedSteps}/{companyProgress.totalSteps}
                        </div>
                        <div className={styles.progressLine}>
                          <div
                            className={styles.progressBar}
                            style={{
                              backgroundColor: companyProgress.progressColor,
                              width: companyProgress.progressWidth,
                            }}
                          ></div>
                        </div>
                      </div>
                      <div className={styles.progressLabel}>
                        {companyProgress.progressLabel}
                      </div>
                    </div>
                  </div>

                  {/* Centre de formation */}
                  <div 
                    className={styles.item} 
                    style={{ backgroundColor: "#edf8f2", cursor: center?.id ? 'pointer' : 'default' }}
                    onClick={handleNavigateToCenter}
                  >
                    <div className={styles.avatar}>
                      <img src="/images/content/school_logo.png" alt="Centre" />
                    </div>
                    <div className={styles.line}>
                      <div className={styles.details}>
                        <div className={styles.indicator}>
                          <span>Centre de formation </span>
                        </div>
                        <div className={styles.category}>
                          {center?.name}
                        </div>
                      </div>
                    </div>
                    <div className={styles.progressContainer}>
                      <div className={styles.progressBox}>
                        <div
                          className={styles.progressNumber}
                          style={{ color: schoolProgress.progressColor }}
                        >
                          {schoolProgress.completedSteps}/{schoolProgress.totalSteps}
                        </div>
                        <div className={styles.progressLine}>
                          <div
                            className={styles.progressBar}
                            style={{
                              backgroundColor: schoolProgress.progressColor,
                              width: schoolProgress.progressWidth,
                            }}
                          ></div>
                        </div>
                      </div>
                      <div className={styles.progressLabel}>
                        {schoolProgress.progressLabel}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.control}>
                <div className={cn(styles.nav, styles.desktopNav)}>
                  {navigation.map((x, index) => (
                    <button
                      className={cn(styles.link, {
                        [styles.active]: index === activeIndex,
                      })}
                      onClick={() => setActiveIndex(index)}
                      key={index}
                    >
                      {x}
                    </button>
                  ))}
                </div>

                <div className={styles.mobileNav}>
                  <Dropdown
                    className={styles.dropdown}
                    value={navigation[activeIndex]}
                    setValue={(value) => {
                      const index = navigation.findIndex(x => x === value);
                      if (index !== -1) setActiveIndex(index);
                    }}
                    options={navigation}
                  />
                </div>
              </div>
            </Card>
          </>
        )}

        <div className={styles.wrap}>
          {activeIndex === 0 && folder && <FolderProgress folder={folder} onFolderUpdate={loadFolder} />}
          {activeIndex === 1 && folder && <ContractProgress folderId={folder.id} onFolderUpdate={loadFolder} />}
          {activeIndex === 2 && folder && <Comptability folderId={folder.id} onFolderUpdate={loadFolder} />}
        </div>

      </div>
    </>
  );
};

export default FolderDetail;